import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { adminGetVapidKey, adminSubscribePush, adminUnsubscribePush, adminTestPush } from '@/lib/api';

// Convert URL-safe base64 to a Uint8Array for PushManager.subscribe
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function subToJson(subscription) {
  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  const b64 = (buf) => btoa(String.fromCharCode.apply(null, new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: b64(key), auth: b64(auth) },
  };
}

/**
 * Small control panel shown in Admin Dashboard header that:
 * - Detects push notification support
 * - Prompts for permission
 * - Subscribes and stores the subscription server-side
 * - Provides an unsubscribe + a "send test push" button
 */
export default function PushSubscribe({ testIdSuffix = '' }) {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const [permission, setPermission] = useState(supported ? Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const tid = (base) => (testIdSuffix ? `${base}-${testIdSuffix}` : base);

  const refreshStatus = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      setSubscribed(!!existing);
    } catch (_e) {
      setSubscribed(false);
    }
    setPermission(Notification.permission);
  }, [supported]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const enableNotifications = async () => {
    if (!supported) {
      toast.error('This browser does not support push notifications');
      return;
    }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Notifications blocked — enable them in your browser settings.');
        return;
      }
      const keyRes = await adminGetVapidKey();
      const vapidPublic = keyRes.data.public_key;
      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublic),
        });
      }
      await adminSubscribePush(subToJson(subscription));
      setSubscribed(true);
      toast.success('Notifications enabled on this device.');
    } catch (err) {
      console.error('Push subscribe failed', err);
      toast.error('Could not enable notifications: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const disableNotifications = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await adminUnsubscribePush(subToJson(subscription));
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      toast.success('Notifications disabled on this device.');
    } catch (err) {
      toast.error('Could not disable: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const testPush = async () => {
    setBusy(true);
    try {
      await adminTestPush();
      toast.success('Test push sent — should arrive in a second or two.');
    } catch (_e) {
      toast.error('Test push failed');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid={tid('push-unsupported')}>
        <BellOff className="w-4 h-4" />
        Push notifications not supported on this browser
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={tid('push-controls')}>
      {subscribed ? (
        <>
          <span className="flex items-center gap-1.5 text-xs text-green-500 uppercase tracking-wider">
            <BellRing className="w-4 h-4" />
            Notifications On
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={testPush}
            disabled={busy}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
            data-testid={tid('push-test-btn')}
          >
            Send Test
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={disableNotifications}
            disabled={busy}
            className="text-zinc-500 hover:text-red-400 text-xs"
            data-testid={tid('push-disable-btn')}
          >
            Disable
          </Button>
        </>
      ) : permission === 'denied' ? (
        <div
          className="flex items-center gap-1.5 text-xs text-zinc-500 border border-zinc-800 bg-zinc-900 px-3 py-1.5"
          data-testid={tid('push-blocked')}
          title="Enable notifications in your browser or Android app settings for this site"
        >
          <BellOff className="w-4 h-4" />
          Notifications Blocked
        </div>
      ) : (
        <Button
          onClick={enableNotifications}
          disabled={busy}
          className="bg-green-600 hover:bg-green-700 text-white text-xs"
          size="sm"
          data-testid={tid('push-enable-btn')}
        >
          <Bell className="w-4 h-4 mr-1.5" />
          Enable Notifications
        </Button>
      )}
    </div>
  );
}
